import { Button, Checkbox, Input } from "@heroui/react";
import { useServerFn } from "@tanstack/react-start";

import { AltaviaLogoBigPNG } from "@onesolution/assets";
import { LanguageSelect, Logo, ThemeSwitcher } from "@onesolution/components";
import { useLanguage } from "@onesolution/hooks";
import { getLoginSsoUrl } from "@onesolution/serverFn/auth/getLoginSsoUrl";
import { isValidEmail } from "@onesolution/utils";
import { useNavigate } from "@tanstack/react-router";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";

const cookieKey = "sso-email";
export default function LoginSsoPage() {
	const navigate = useNavigate();
	const getLoginSsoUrlCall = useServerFn(getLoginSsoUrl);
	const storedEmail = Cookies.get(cookieKey);
	const [remember, setRemember] = useState(false);
	const [email, setEmail] = useState("");
	const [error, setError] = useState("");
	const [providers, setProviders] = useState<
		{ provider: string; url: string }[]
	>([]);
	const { t } = useLanguage();
	useEffect(() => {
		setTimeout(() => {
			//weird stuff without this
			setRemember(!!storedEmail);
			setEmail(storedEmail || "");
		}, 100);
	}, [storedEmail]);

	return (
		<div>
			<div className="grid grid-cols-1 md:grid-cols-2 h-screen py-6">
				<div className="flex flex-col justify-between items-center py-4 px-6 md:pl-10">
					<div className="self-start">
						<Logo />
					</div>

					<div className="flex flex-col gap-5 w-full md:w-[420px]">
						<div className="flex flex-col gap-1">
							<h2 className="text-xl font-semibold">{t("login.welcome")},</h2>
							<h4 className="text-sm text-content4-foreground">
								{t("login.continue")}
							</h4>
						</div>
						{!providers.length && (
							<Input
								type="email"
								autoComplete="email"
								inputMode="email"
								value={email}
								onValueChange={setEmail}
								label={t("name.email")}
								labelPlacement="outside-top"
							/>
						)}
						<Checkbox
							isSelected={remember}
							defaultSelected
							onValueChange={setRemember}
						>
							{t("login.remember")}
						</Checkbox>
						{error && <h4 className="text-danger-500">{error}</h4>}
						{providers.length > 0 && (
							<div className="flex gap-2 flex-col">
								{providers.map((p) => (
									<Button
										color="primary"
										onPress={() => navigate({ href: p.url })}
										key={p.provider}
									>
										{p.provider}
									</Button>
								))}
							</div>
						)}
						{providers.length ? (
							<Button
								onPress={async () => {
									setProviders([]);
									setError("");
								}}
								size="lg"
								className="w-full"
							>
								{t("basic.back")}
							</Button>
						) : (
							<>
								<Button
									color={isValidEmail(email) ? "primary" : "default"}
									onPress={async () => {
										setError("");
										setProviders([]);
										const urls = await getLoginSsoUrlCall({ data: { email } });
										if (typeof urls === "string")
											return setError(t("login.sso-error"));
										if (!urls) return setError(t("login.sso-error"));
										if (urls?.length === 1) {
											localStorage.setItem("SSO-LAST-TRY", email);

											if (remember) {
												Cookies.set(cookieKey, email, {
													expires: 365 * 100,
													sameSite: "Lax",
												});
											} else {
												Cookies.remove(cookieKey);
											}

											return navigate({ href: urls[0].url });
										}

										setProviders(urls);
									}}
									size="lg"
									disabled={!isValidEmail(email)}
									className="w-full"
								>
									{t("basic.next")}
								</Button>
								<Button
									onPress={async () => {
										navigate({ href: "/login" });
									}}
									size="lg"
									className="w-full"
								>
									{t("basic.back")}
								</Button>
							</>
						)}
					</div>

					<div className="flex items-center w-full gap-1">
						<LanguageSelect collapsed />
						<ThemeSwitcher />
					</div>
				</div>

				<img
					aria-label="Altavia logo velvet"
					className="hidden md:block size-full overflow-hidden rounded-s-3xl shadow-2xl object-cover mix-blend-luminosity"
					src={AltaviaLogoBigPNG}
				/>
			</div>
		</div>
	);
}
